import * as R from "ramda";


const separatorChar = ",";


function areIdSegmentsEqual(ids1: any, ids2: any): boolean {
    return (ids1 === null || ids1 === undefined
        ? ids2 === null || ids2 === undefined
        : typeof ids1 !== "string" || typeof ids2 !== "string"
            ? ids1 === ids2
            : ids1.toLowerCase() === ids2.toLowerCase());
}


/**
 * Compares IDs for equality. IDs can either be single or composite keys.
 */
export function areEqual(id1: any, id2: any): boolean {
    if (!Array.isArray(id1)) {
        return !Array.isArray(id2) && areIdSegmentsEqual(id1, id2);
    }
    if (!Array.isArray(id2) || id1.length !== id2.length) {
        return false;
    }

    for (let i = 0, l = id1.length; i < l; i += 1) {
        if (!areIdSegmentsEqual(id1[i], id2[i])) {
            return false;
        }
    }
    return true;
}


/**
 * Decodes URI component encoded with "crumbId.encode" into individual segments.
 *
 * @param value {string}
 *     Encoded URI fragment containing one or more segments
 *     separated by the separator character (","). For example,
 *     value "Hello%2C%20world,World%2C%20hello" will be
 *     decoded into ["Hello, world", "World, hello"].
 *
 * @returns {string | string[]}
 *     A string if result has a single segment, array of strings otherwise.
 */
export function decode(value: string): string | string[] {
    if (typeof value !== "string") { return ""; }

    const result = R.map(decodeURIComponent, value.split(separatorChar));
    return (result.length === 1 ? result[0] : result);
}


/**
 * Encodes a single segment of crumb ID into a URI component string.
 */
function encodeIdSegment(value: any): string {
    return (value === null || value === undefined
        ? ""
        : encodeURIComponent(value));
}


/**
 * Encodes ID containing one (simple key) or more (composite key) segments into a single URI component.
 *
 * Try to keep the result as pretty (for humans) as possible.
 * Accepts 0..N arguments; use one argument for regular keys,
 * multiple arguments for composite keys.
 */
export function encode(ids: any): string {
    return (arguments.length === 1 && Array.isArray(ids)
        ? encode.apply(null, ids) // special case for "encodeId([1,2,3])", unwrapped to "encodeId(1,2,3)"
        : R.join(separatorChar, R.map(encodeIdSegment, arguments)));
}


/**
 * Parsed crumb ID = main ID value + extra named and anonymous parameters.
 *
 * Parsed crumb IDs give structure to decoded IDs that are
 * stored on individual crumb objects maintained by "Forms.js".
 *
 * Application developers typically convert raw decoded IDs
 * to structured IDs by calling "parseDecodedCrumbId(targetCrumb.data.id)".
 * In "DocumentViewModel" subclasses you can parse root crumb ID
 * (ID of the document to be loaded) in "DocumentViewModel.loading(crumbData)"
 * method override by calling "parseDecodedCrumbId(crumbData.id)".
 *
 * @example
 * ["invoice:42"] => { id: "invoice:42", named: {}, anonymous: [] }
 * ["invoice:42", "title:expensive"] => { id: "invoice:42", named: { title: "expensive" }, anonymous: [] }
 * ["invoice:42", "4", "2"] => { id: "invoice:42", named: {}, anonymous: ["4", "2"] }
 */
export interface ParsedCrumbId {
    /**
     * Main ID of this crumb. Can contain arbitrary characters including ":".
     */
    id: string;

    /**
     * Named parameters. Names cannot contain ":", values can.
     */
    named: { [name: string]: string };

    /**
     * Anonymous (unnamed) parameters. Cannot contain ":".
     */
    anonymous: string[];
}


/**
 * Parses the specified crumb ID (typically created by "crumbId.decode", e.g. from "Forms.js") into a new "ParsedCrumbId" object.
 */
export function parseDecodedCrumbId(decodedCrumbId: string | string[]): ParsedCrumbId {
    if (decodedCrumbId === undefined || decodedCrumbId === null) {
        return { id: "", named: {}, anonymous: [] };
    }
    if (typeof decodedCrumbId === "string") {
        return { id: decodedCrumbId, named: {}, anonymous: [] };
    }

    const result: ParsedCrumbId = {
        id: "",
        named: {},
        anonymous: []
    };

    for (const crumbIdSegment of decodedCrumbId) {
        if (!result.id) { // no ID yet, set it (do not parse first segment)
            result.id = crumbIdSegment;
        }
        else {
            const separator = crumbIdSegment.indexOf(":");
            if (separator < 0) {
                result.anonymous.push(crumbIdSegment);
            } else {
                result.named[crumbIdSegment.substring(0, separator)] = crumbIdSegment.substring(separator + 1);
            }
        }
    }

    return result;
}


/**
 * Serializes "ParsedCrumbId" object into an array of crumb ID segments (which can be used as an input to "crumbId.encode" or "Forms.js/open" functions).
 */
export function stringifyParsedCrumbId(parsedCrumbId: ParsedCrumbId): string[] {
    if (parsedCrumbId === undefined || parsedCrumbId === null) {
        return [""];
    }

    return [parsedCrumbId.id]
        .concat(parsedCrumbId.anonymous)
        .concat(Object
            .keys(parsedCrumbId.named)
            .map(name => `${name}:${parsedCrumbId.named[name]}`));
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/util/crumbId.ts